/*
 * Copyright (C) 2026 Fluxer Contributors
 *
 * This file is part of Fluxer.
 *
 * Fluxer is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * Fluxer is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with Fluxer. If not, see <https://www.gnu.org/licenses/>.
 */

import * as DraftActionCreators from '@app/actions/DraftActionCreators';
import * as MessageActionCreators from '@app/actions/MessageActionCreators';
import * as ModalActionCreators from '@app/actions/ModalActionCreators';
import {modal} from '@app/actions/ModalActionCreators';
import * as SlowmodeActionCreators from '@app/actions/SlowmodeActionCreators';
import {AttachmentUploadConnectivityModal} from '@app/components/alerts/AttachmentUploadConnectivityModal';
import {FeatureTemporarilyDisabledModal} from '@app/components/alerts/FeatureTemporarilyDisabledModal';
import {FileSizeTooLargeModal} from '@app/components/alerts/FileSizeTooLargeModal';
import {MessageEditFailedModal} from '@app/components/alerts/MessageEditFailedModal';
import {MessageEditTooQuickModal} from '@app/components/alerts/MessageEditTooQuickModal';
import {MessageSendTooQuickModal} from '@app/components/alerts/MessageSendTooQuickModal';
import {NSFWContentRejectedModal} from '@app/components/alerts/NSFWContentRejectedModal';
import {SlowmodeRateLimitedModal} from '@app/components/alerts/SlowmodeRateLimitedModal';
import {Endpoints} from '@app/Endpoints';
import i18n from '@app/I18n';
import {ApiResponseError} from '@app/lib/ApiResponseError';
import {
	type ChunkedUploadPart,
	type ChunkedUploadPlan,
	uploadFileInChunks,
} from '@app/lib/ChunkedAttachmentUploader';
import {CloudUpload} from '@app/lib/CloudUpload';
import http, {type HttpResponse} from '@app/lib/HttpClient';
import {Logger} from '@app/lib/Logger';
import {Queue, type QueueEntry} from '@app/lib/Queue';
import DeveloperOptionsStore from '@app/stores/DeveloperOptionsStore';
import RuntimeConfigStore from '@app/stores/RuntimeConfigStore';
import {exceedsMultipartFallbackRequestSize} from '@app/utils/AttachmentUploadFallbackUtils';
import {createSystemMessage} from '@app/utils/CommandUtils';
import {prepareAttachmentsForNonce} from '@app/utils/MessageAttachmentUtils';
import {
	type ApiAttachmentMetadata,
	buildMessageCreateRequest,
	type MessageCreateRequest,
	type MessageEditRequest,
} from '@app/utils/MessageRequestUtils';
import {APIErrorCodes} from '@fluxer/constants/src/ApiErrorCodes';
import type {
	AllowedMentions,
	Message,
	MessageReference,
	MessageStickerItem,
} from '@fluxer/schema/src/domains/message/MessageResponseSchemas';
import type {I18n} from '@lingui/core';
import {msg} from '@lingui/core/macro';

const logger = new Logger('MessageQueue');

const DEFAULT_MAX_SIZE = 5;
const DEV_MESSAGE_DELAY = 3000;

interface BaseMessagePayload {
	channelId: string;
}

interface SendMessagePayload extends BaseMessagePayload {
	type: 'send';
	nonce: string;
	content: string;
	hasAttachments?: boolean;
	/** Finalized attachment metadata ready for the message-create request. */
	preparedAttachments?: Array<ApiAttachmentMetadata>;
	/** Files to send via multipart fallback when presigned uploads couldn't be used. */
	preparedFiles?: Array<File>;
	allowedMentions?: AllowedMentions;
	messageReference?: MessageReference;
	flags?: number;
	favoriteMemeId?: string;
	stickers?: Array<MessageStickerItem>;
	tts?: boolean;
}

interface EditMessagePayload extends BaseMessagePayload {
	type: 'edit';
	messageId: string;
	content?: string;
	flags?: number;
}

export type MessageQueuePayload = SendMessagePayload | EditMessagePayload;

export interface RetryError {
	retryAfter?: number;
}

export interface ApiErrorBody {
	code?: number | string;
	retry_after?: number;
	message?: string;
}

interface PresignedAttachmentUploadSinglepartResponse {
	upload_mode: 'singlepart';
	id: string | number;
	filename: string;
	upload_filename: string;
	upload_url: string;
	file_size: number;
	content_type: string;
}

interface PresignedAttachmentUploadMultipartResponse {
	upload_mode: 'multipart';
	id: string | number;
	filename: string;
	upload_filename: string;
	file_size: number;
	content_type: string;
	upload_id: string;
	part_size: number;
	parts: Array<{part_number: number; upload_url: string}>;
}

type PresignedAttachmentUploadResponseAttachment =
	| PresignedAttachmentUploadSinglepartResponse
	| PresignedAttachmentUploadMultipartResponse;

interface PresignedAttachmentUploadRequestFile {
	id: string;
	filename: string;
	file_size: number;
	content_type: string;
}

interface PresignedAttachmentUploadRequestBody {
	attachments: Array<PresignedAttachmentUploadRequestFile>;
}

interface PresignedAttachmentUploadResponseBody {
	attachments: Array<PresignedAttachmentUploadResponseAttachment>;
}

interface CompleteMultipartAttachmentUploadRequestBody {
	uploads: Array<{upload_filename: string; upload_id: string}>;
}

interface CompleteMultipartAttachmentUploadResponseBody {
	uploads: Array<{upload_filename: string}>;
}

export interface PreparedSendAttachments {
	attachments?: Array<ApiAttachmentMetadata>;
	files?: Array<File>;
}

const getApiErrorBody = (error: ApiResponseError): ApiErrorBody | undefined => {
	return typeof error?.payload === 'object' && error.payload !== null ? (error.payload as ApiErrorBody) : undefined;
};

const isAbortError = (error: unknown): boolean => {
	return error instanceof DOMException && error.name === 'AbortError';
};

const isTimeoutError = (error: unknown): boolean => {
	return error instanceof DOMException && error.name === 'TimeoutError';
};

const isNetworkRequestError = (error: unknown): boolean => {
	return error instanceof Error && error.message === 'Network error during request';
};

function isSendPayload(payload: MessageQueuePayload): payload is SendMessagePayload {
	return payload.type === 'send';
}

function isEditPayload(payload: MessageQueuePayload): payload is EditMessagePayload {
	return payload.type === 'edit';
}

function isRateLimitError(error: ApiResponseError): boolean {
	return error?.statusCode === 429;
}

function isSlowmodeError(error: ApiResponseError): boolean {
	return error?.statusCode === 400 && getApiErrorBody(error)?.code === APIErrorCodes.SLOWMODE_RATE_LIMITED;
}

function isFeatureDisabledError(error: ApiResponseError): boolean {
	return error?.statusCode === 403 && getApiErrorBody(error)?.code === APIErrorCodes.FEATURE_TEMPORARILY_DISABLED;
}

function isExplicitContentError(error: ApiResponseError): boolean {
	return getApiErrorBody(error)?.code === APIErrorCodes.EXPLICIT_CONTENT_CANNOT_BE_SENT;
}

function isFileTooLargeError(error: ApiResponseError): boolean {
	return getApiErrorBody(error)?.code === APIErrorCodes.FILE_SIZE_TOO_LARGE;
}

function isDMRestrictedError(error: ApiResponseError): boolean {
	return getApiErrorBody(error)?.code === APIErrorCodes.CANNOT_SEND_MESSAGES_TO_USER;
}

function getUnclaimedAccountErrorCode(error: ApiResponseError): string | undefined {
	const code = getApiErrorBody(error)?.code;
	if (
		code === APIErrorCodes.UNCLAIMED_ACCOUNT_CANNOT_SEND_MESSAGES ||
		code === APIErrorCodes.UNCLAIMED_ACCOUNT_CANNOT_SEND_DIRECT_MESSAGES
	) {
		return code;
	}
	return undefined;
}

function isPresignedUploadEndpointUnreachable(error: unknown): boolean {
	if (isTimeoutError(error)) {
		return true;
	}

	if (error instanceof ApiResponseError) {
		return typeof error.statusCode === 'number' && error.statusCode >= 500;
	}

	return isNetworkRequestError(error);
}

class PresignedUploadFallbackUnavailableError extends Error {
	constructor() {
		super('Presigned attachment upload request failed and multipart fallback is unavailable');
		this.name = 'PresignedUploadFallbackUnavailableError';
	}
}

export class MessageQueue extends Queue<MessageQueuePayload, HttpResponse<Message> | undefined> {
	private readonly maxSize: number;
	private readonly abortControllers = new Map<string, AbortController>();

	constructor(maxSize = DEFAULT_MAX_SIZE) {
		super({logger, defaultRetryAfter: 100});
		this.maxSize = maxSize;
	}

	isFull(): boolean {
		return this.queueLength >= this.maxSize;
	}

	drain(
		message: MessageQueuePayload,
		completed: (err: RetryError | null, result?: HttpResponse<Message>, error?: unknown) => void,
	): Promise<unknown> | undefined {
		if (isSendPayload(message)) {
			return this.handleSend(message, completed);
		} else if (isEditPayload(message)) {
			return this.handleEdit(message, completed);
		} else {
			logger.error('Unknown message type, completing with null');
			completed(null, undefined, new Error('Unknown message queue payload'));
			return undefined;
		}
	}

	cancelRequest(nonce: string): void {
		logger.info('Cancel message send:', nonce);
		const controller = this.abortControllers.get(nonce);
		controller?.abort();
		this.abortControllers.delete(nonce);
	}

	cancelPendingSendRequests(channelId: string): Array<SendMessagePayload> {
		const cancelled: Array<SendMessagePayload> = [];
		const remaining: Array<QueueEntry<MessageQueuePayload, HttpResponse<Message> | undefined>> = [];

		while (this.queue.length > 0) {
			const entry = this.queue.shift()!;

			if (isSendPayload(entry.message) && entry.message.channelId === channelId) {
				cancelled.push(entry.message);
				this.cancelRequest(entry.message.nonce);
			} else {
				remaining.push(entry);
			}
		}

		this.queue.push(...remaining);
		logger.info('Cancel pending send requests', cancelled.length);
		return cancelled;
	}

	/**
	 * Runs attachment upload work *before* a message is enqueued so that
	 * pending uploads do not block plain-text sends sharing the same queue.
	 * Returns finalized attachment metadata (presigned path) or the original
	 * files (multipart-fallback path) for handleSend to include in the POST.
	 * Returns null on terminal failure — the failure has already been reported
	 * via handleSendError and the caller should resolve the send as null.
	 */
	async prepareAttachmentsForSend(params: {
		channelId: string;
		nonce: string;
		favoriteMemeId?: string;
	}): Promise<PreparedSendAttachments | null> {
		const {channelId, nonce, favoriteMemeId} = params;

		const abortController = new AbortController();
		this.abortControllers.set(nonce, abortController);

		try {
			const {attachments: rawAttachments, files} = await prepareAttachmentsForNonce(nonce, favoriteMemeId);

			if (!files?.length || !rawAttachments?.length) {
				return {attachments: rawAttachments, files};
			}

			if (!this.canUsePresignedAttachmentUploads(files)) {
				return {attachments: rawAttachments, files};
			}

			if (rawAttachments.length !== files.length) {
				throw new Error(
					`Attachment metadata mismatch for presigned uploads: expected ${files.length} entries but got ${rawAttachments.length}`,
				);
			}

			let plans: Array<PresignedAttachmentUploadResponseAttachment>;
			try {
				plans = await this.requestPresignedAttachmentUploads(
					channelId,
					rawAttachments,
					files,
					abortController.signal,
				);
			} catch (error) {
				if (isAbortError(error)) {
					return null;
				}

				if (isPresignedUploadEndpointUnreachable(error)) {
					if (exceedsMultipartFallbackRequestSize(files)) {
						logger.warn(
							'Presigned attachment upload URL request failed because the endpoint was unreachable and multipart fallback cannot be used for oversized requests',
							error,
						);
						this.handleSendError(channelId, nonce, new PresignedUploadFallbackUnavailableError(), i18n, true);
						return null;
					}
					logger.warn(
						'Presigned attachment upload URL request failed because the endpoint was unreachable; falling back to multipart message upload',
						error,
					);
					return {attachments: rawAttachments, files};
				}

				logger.warn(
					'Presigned attachment upload URL request failed; falling back to multipart message upload',
					error,
				);
				return {attachments: rawAttachments, files};
			}

			const planIndexById = new Map<string, number>();
			plans.forEach((entry, index) => planIndexById.set(String(entry.id), index));
			for (const attachment of rawAttachments) {
				if (!planIndexById.has(String(attachment.id))) {
					this.handleSendError(
						channelId,
						nonce,
						new Error(`Missing presigned upload metadata for attachment ${attachment.id}`),
						i18n,
						true,
					);
					return null;
				}
			}

			const multipartUploadsToComplete: Array<{upload_filename: string; upload_id: string}> = [];

			let finalized: Array<ApiAttachmentMetadata>;
			try {
				finalized = await this.uploadAttachmentsViaPlans({
					nonce,
					attachments: rawAttachments,
					files,
					plans,
					planIndexById,
					multipartUploadsToComplete,
					signal: abortController.signal,
				});
			} catch (error) {
				if (isAbortError(error)) {
					this.abortRemainingMultipartUploads(channelId, multipartUploadsToComplete);
					return null;
				}

				if (exceedsMultipartFallbackRequestSize(files)) {
					logger.warn(
						'Presigned attachment upload failed and multipart fallback cannot be used for oversized requests',
						error,
					);
					this.handleSendError(channelId, nonce, new PresignedUploadFallbackUnavailableError(), i18n, true);
					return null;
				}

				logger.warn('Presigned attachment upload failed; falling back to multipart message upload', error);
				CloudUpload.updateSendingProgress(nonce, 0);
				this.abortRemainingMultipartUploads(channelId, multipartUploadsToComplete);
				return {attachments: rawAttachments, files};
			}

			if (multipartUploadsToComplete.length > 0) {
				try {
					await this.completeMultipartAttachmentUploads(channelId, multipartUploadsToComplete, abortController.signal);
				} catch (error) {
					if (isAbortError(error)) {
						return null;
					}
					logger.error(
						`Failed to finalize multipart attachment uploads for channel ${channelId}`,
						error,
					);
					this.handleSendError(channelId, nonce, error, i18n, true);
					return null;
				}
			}

			return {attachments: finalized, files: undefined};
		} catch (error) {
			if (isAbortError(error)) {
				return null;
			}
			logger.error(`Failed to prepare attachments for channel ${channelId}:`, error);
			this.handleSendError(channelId, nonce, error, i18n, true);
			return null;
		} finally {
			// Keep the abort controller in place so the subsequent POST also
			// responds to cancelRequest; it will be replaced/cleaned up by
			// sendMessageRequest, or here if we aren't enqueueing. The caller
			// is expected to clean up via cancelRequest on error exit.
			if (this.abortControllers.get(nonce) === abortController && abortController.signal.aborted) {
				this.abortControllers.delete(nonce);
			}
		}
	}

	private async handleSend(
		payload: SendMessagePayload,
		completed: (err: RetryError | null, result?: HttpResponse<Message>, error?: unknown) => void,
	): Promise<void> {
		const {channelId, nonce, hasAttachments} = payload;

		await this.applyDevDelay();

		if (DeveloperOptionsStore.forceFailMessageSends) {
			const forcedError = new Error('Forced message send failure');
			logger.error(`Failed to send message to channel ${channelId}:`, forcedError);
			this.handleSendError(channelId, nonce, forcedError as ApiResponseError, i18n, payload.hasAttachments);
			completed(null, undefined, forcedError);
			return;
		}

		const requestBody = buildMessageCreateRequest({
			content: payload.content,
			nonce,
			attachments: payload.preparedAttachments,
			allowedMentions: payload.allowedMentions,
			messageReference: payload.messageReference,
			flags: payload.flags,
			favoriteMemeId: payload.favoriteMemeId,
			stickers: payload.stickers,
			tts: payload.tts,
		});

		logger.debug(`Sending message to channel ${channelId}`);

		const outcome = await this.attemptMessageSend(channelId, nonce, requestBody, payload.preparedFiles);

		if (outcome.status === 'success') {
			logger.debug(`Successfully sent message to channel ${channelId}`);

			if (hasAttachments) {
				CloudUpload.removeMessageUpload(nonce);
			}

			completed(null, outcome.response);
			return;
		}

		logger.error(`Failed to send message to channel ${channelId}:`, outcome.error);

		if (outcome.status === 'rateLimit') {
			this.handleSendRateLimit(outcome.error, completed);
		} else {
			this.handleSendError(channelId, nonce, outcome.error, i18n, payload.hasAttachments);
			completed(null, undefined, outcome.error);
		}
	}

	private async applyDevDelay(): Promise<void> {
		if (!DeveloperOptionsStore.slowMessageSend) return;

		logger.debug(`Slow message send enabled, delaying by ${DEV_MESSAGE_DELAY}ms`);
		await new Promise((resolve) => setTimeout(resolve, DEV_MESSAGE_DELAY));
	}

	private async sendMessageRequest(
		channelId: string,
		nonce: string,
		requestBody: MessageCreateRequest,
		files?: Array<File>,
	): Promise<HttpResponse<Message>> {
		const existing = this.abortControllers.get(nonce);
		const abortController = existing ?? new AbortController();
		if (!existing) {
			this.abortControllers.set(nonce, abortController);
		}

		try {
			if (files?.length) {
				logger.debug('Sending message with multipart form data');
				return await this.sendMultipartMessage(channelId, requestBody, files, abortController.signal, nonce);
			}

			return await http.post<Message>({
				url: Endpoints.CHANNEL_MESSAGES(channelId),
				body: requestBody,
				signal: abortController.signal,
				rejectWithError: true,
				skipContentBlockedModal: true,
			});
		} finally {
			this.abortControllers.delete(nonce);
		}
	}

	private canUsePresignedAttachmentUploads(files?: Array<File>): files is Array<File> {
		return RuntimeConfigStore.features.presigned_attachment_uploads && Boolean(files?.length);
	}

	private async requestPresignedAttachmentUploads(
		channelId: string,
		attachments: Array<ApiAttachmentMetadata>,
		files: Array<File>,
		signal: AbortSignal,
	): Promise<Array<PresignedAttachmentUploadResponseAttachment>> {
		const requestBody: PresignedAttachmentUploadRequestBody = {
			attachments: attachments.map((attachment, index) => ({
				id: attachment.id,
				filename: attachment.filename,
				file_size: files[index].size,
				content_type: files[index].type || 'application/octet-stream',
			})),
		};

		const response = await http.post<PresignedAttachmentUploadResponseBody>({
			url: Endpoints.CHANNEL_ATTACHMENTS(channelId),
			body: requestBody,
			signal,
			rejectWithError: true,
		});

		const plans = response.body?.attachments ?? [];

		for (const entry of plans) {
			if (!entry || !entry.upload_mode || !entry.upload_filename || !entry.filename) {
				throw new Error('Invalid presigned attachment upload response');
			}
			if (entry.upload_mode === 'singlepart') {
				if (!entry.upload_url) {
					throw new Error(`Missing upload_url for singlepart attachment ${entry.id}`);
				}
			} else if (entry.upload_mode === 'multipart') {
				if (!entry.upload_id || !Array.isArray(entry.parts) || entry.parts.length === 0) {
					throw new Error(`Missing multipart metadata for attachment ${entry.id}`);
				}
			} else {
				throw new Error(`Unknown upload_mode for attachment ${(entry as {id: string}).id}`);
			}
		}

		return plans;
	}

	private async uploadAttachmentsViaPlans(params: {
		nonce: string;
		attachments: Array<ApiAttachmentMetadata>;
		files: Array<File>;
		plans: Array<PresignedAttachmentUploadResponseAttachment>;
		planIndexById: Map<string, number>;
		multipartUploadsToComplete: Array<{upload_filename: string; upload_id: string}>;
		signal: AbortSignal;
	}): Promise<Array<ApiAttachmentMetadata>> {
		const {nonce, attachments, files, plans, planIndexById, multipartUploadsToComplete, signal} = params;

		const totalBytes = files.reduce((sum, file) => sum + file.size, 0);
		const loadedBytesByIndex = new Array<number>(files.length).fill(0);
		let completedUploads = 0;

		const reportProgress = (): void => {
			if (totalBytes > 0) {
				const uploadedBytes = loadedBytesByIndex.reduce((sum, loaded) => sum + loaded, 0);
				CloudUpload.updateSendingProgress(nonce, (uploadedBytes / totalBytes) * 100);
				return;
			}
			if (files.length > 0) {
				CloudUpload.updateSendingProgress(nonce, (completedUploads / files.length) * 100);
			}
		};

		for (let index = 0; index < attachments.length; index += 1) {
			const attachment = attachments[index];
			const file = files[index];
			const planIndex = planIndexById.get(String(attachment.id));
			if (planIndex == null) {
				throw new Error(`Missing presigned upload metadata for attachment ${attachment.id}`);
			}
			const plan = plans[planIndex];

			if (plan.upload_mode === 'singlepart') {
				await http.put({
					url: plan.upload_url,
					body: file,
					headers: {
						'Content-Type': file.type || 'application/octet-stream',
					},
					signal,
					rejectWithError: true,
					onRequestProgress: (event) => {
						const loaded = Math.min(file.size, event.loaded);
						if (loaded > loadedBytesByIndex[index]) {
							loadedBytesByIndex[index] = loaded;
							reportProgress();
						}
					},
				});
				loadedBytesByIndex[index] = file.size;
			} else {
				multipartUploadsToComplete.push({
					upload_filename: plan.upload_filename,
					upload_id: plan.upload_id,
				});

				const parts: Array<ChunkedUploadPart> = plan.parts.map((entry) => ({
					partNumber: entry.part_number,
					uploadUrl: entry.upload_url,
				}));

				const uploadPlan: ChunkedUploadPlan = {file, partSize: plan.part_size, parts};

				await uploadFileInChunks(uploadPlan, {
					signal,
					onProgress: (uploaded) => {
						if (uploaded > loadedBytesByIndex[index]) {
							loadedBytesByIndex[index] = uploaded;
							reportProgress();
						}
					},
				});
				loadedBytesByIndex[index] = file.size;
			}

			completedUploads += 1;
			reportProgress();
		}

		return attachments.map((attachment) => {
			const planIndex = planIndexById.get(String(attachment.id));
			if (planIndex == null) {
				throw new Error(`Missing presigned upload metadata for attachment ${attachment.id}`);
			}
			const plan = plans[planIndex];

			return {
				...attachment,
				upload_filename: plan.upload_filename,
				file_size: plan.file_size,
				content_type: plan.content_type,
			};
		});
	}

	private async completeMultipartAttachmentUploads(
		channelId: string,
		uploads: Array<{upload_filename: string; upload_id: string}>,
		signal: AbortSignal,
	): Promise<void> {
		const body: CompleteMultipartAttachmentUploadRequestBody = {uploads};
		await http.post<CompleteMultipartAttachmentUploadResponseBody>({
			url: Endpoints.CHANNEL_ATTACHMENTS_COMPLETE(channelId),
			body,
			signal,
			rejectWithError: true,
		});
	}

	private abortRemainingMultipartUploads(
		channelId: string,
		uploads: Array<{upload_filename: string; upload_id: string}>,
	): void {
		if (uploads.length === 0) return;
		// Best-effort: the server garbage-collects abandoned multipart uploads,
		// and there is no public client-facing abort endpoint. Log so we can
		// observe frequency in dev.
		logger.debug(`Leaving ${uploads.length} multipart upload(s) for server to GC in channel ${channelId}`);
	}

	private async sendMultipartMessage(
		channelId: string,
		requestBody: MessageCreateRequest,
		files: Array<File>,
		signal: AbortSignal,
		nonce?: string,
	): Promise<HttpResponse<Message>> {
		const formData = new FormData();
		formData['append']('payload_json', JSON.stringify(requestBody));

		files.forEach((file, index) => {
			formData['append'](`files[${index}]`, file);
		});

		return http.post<Message>({
			url: Endpoints.CHANNEL_MESSAGES(channelId),
			body: formData,
			signal,
			rejectWithError: true,
			skipContentBlockedModal: true,
			onRequestProgress: nonce
				? (event) => {
						if (event.lengthComputable && event.total > 0) {
							const progress = (event.loaded / event.total) * 100;
							CloudUpload.updateSendingProgress(nonce, progress);
						}
					}
				: undefined,
		});
	}

	private async attemptMessageSend(
		channelId: string,
		nonce: string,
		requestBody: MessageCreateRequest,
		files?: Array<File>,
	): Promise<
		| {status: 'success'; response: HttpResponse<Message>}
		| {status: 'rateLimit'; error: ApiResponseError}
		| {status: 'failure'; error: unknown}
	> {
		try {
			const response = await this.sendMessageRequest(channelId, nonce, requestBody, files);
			return {status: 'success', response};
		} catch (error) {
			return this.buildSendOutcome(error);
		}
	}

	private buildSendOutcome(
		error: unknown,
	): {status: 'rateLimit'; error: ApiResponseError} | {status: 'failure'; error: unknown} {
		const responseErr = error instanceof ApiResponseError ? error : null;

		if (responseErr && isRateLimitError(responseErr)) {
			return {status: 'rateLimit', error: responseErr};
		}

		return {status: 'failure', error};
	}

	private handleSendRateLimit(
		error: ApiResponseError,
		completed: (err: RetryError | null, result?: HttpResponse<Message>, error?: unknown) => void,
	): void {
		const retryAfterSeconds = getApiErrorBody(error)?.retry_after ?? 0;
		const retryAfterMs = retryAfterSeconds > 0 ? retryAfterSeconds * 1000 : undefined;

		completed({retryAfter: retryAfterMs}, undefined, error);

		this.handleRateLimitError(retryAfterSeconds);
	}

	private handleSendError(
		channelId: string,
		nonce: string,
		error: unknown,
		i18n: I18n,
		hasAttachments?: boolean,
	): void {
		MessageActionCreators.sendError(channelId, nonce);

		if (hasAttachments) {
			this.restoreFailedMessage(channelId, nonce);
		}

		if (!(error instanceof ApiResponseError)) {
			this.showErrorModal(error, channelId);
			return;
		}

		if (isDMRestrictedError(error)) {
			const systemMessage = createSystemMessage(
				channelId,
				i18n._(
					msg`Your message could not be delivered. This is usually because you don't share a community with the recipient or the recipient is only accepting direct messages from friends. You may also need to adjust your own direct message privacy settings in User Settings > Privacy Dashboard.`,
				),
			);
			MessageActionCreators.createOptimistic(channelId, systemMessage.toJSON());
			return;
		}

		const unclaimedErrorCode = getUnclaimedAccountErrorCode(error);
		if (unclaimedErrorCode) {
			const systemMessage = createSystemMessage(
				channelId,
				unclaimedErrorCode === APIErrorCodes.UNCLAIMED_ACCOUNT_CANNOT_SEND_DIRECT_MESSAGES
					? i18n._(msg`Your message could not be delivered. You need to claim your account to send direct messages.`)
					: i18n._(msg`Your message could not be delivered. You need to claim your account to send messages.`),
			);
			MessageActionCreators.createOptimistic(channelId, systemMessage.toJSON());
			return;
		}

		if (getApiErrorBody(error)?.code === APIErrorCodes.CONTENT_BLOCKED) {
			const systemMessage = createSystemMessage(
				channelId,
				i18n._(msg`Your message could not be delivered because it was flagged by our safety systems. If you believe this is a mistake, please contact support.`),
			);
			MessageActionCreators.createOptimistic(channelId, systemMessage.toJSON());
			return;
		}

		if (getApiErrorBody(error)?.code === APIErrorCodes.NSFW_EMOJI_STICKER_BLOCKED) {
			const systemMessage = createSystemMessage(
				channelId,
				i18n._(msg`Your message could not be delivered because it contains emoji or stickers classified as NSFW that are not allowed in this context.`),
			);
			MessageActionCreators.createOptimistic(channelId, systemMessage.toJSON());
			return;
		}

		this.showErrorModal(error, channelId);
	}

	private restoreFailedMessage(channelId: string, nonce: string): void {
		const messageUpload = CloudUpload.getMessageUpload(nonce);

		CloudUpload.restoreAttachmentsToTextarea(nonce);

		const contentToRestore = messageUpload?.content ?? '';
		DraftActionCreators.createDraft(channelId, contentToRestore);

		if (messageUpload?.messageReference) {
			MessageActionCreators.startReply(
				channelId,
				messageUpload.messageReference.message_id,
				messageUpload.allowedMentions?.replied_user ?? true,
			);
		}

		MessageActionCreators.deleteOptimistic(channelId, nonce);
	}

	private showErrorModal(error: unknown, channelId?: string): void {
		if (error instanceof PresignedUploadFallbackUnavailableError) {
			ModalActionCreators.push(modal(() => <AttachmentUploadConnectivityModal />));
		} else if (error instanceof ApiResponseError && isSlowmodeError(error)) {
			const retryAfter = Math.ceil(getApiErrorBody(error)?.retry_after ?? 0);
			const timestamp = Date.now() - retryAfter * 1000;
			if (channelId) {
				SlowmodeActionCreators.updateSlowmodeTimestamp(channelId, timestamp);
			}
			ModalActionCreators.push(modal(() => <SlowmodeRateLimitedModal retryAfter={retryAfter} />));
		} else if (error instanceof ApiResponseError && isFeatureDisabledError(error)) {
			ModalActionCreators.push(modal(() => <FeatureTemporarilyDisabledModal />));
		} else if (error instanceof ApiResponseError && isExplicitContentError(error)) {
			ModalActionCreators.push(modal(() => <NSFWContentRejectedModal />));
		} else if (error instanceof ApiResponseError && isFileTooLargeError(error)) {
			ModalActionCreators.push(modal(() => <FileSizeTooLargeModal />));
		}
	}

	private handleRateLimitError(retryAfter: number, onRetry?: () => void): void {
		ModalActionCreators.push(modal(() => <MessageSendTooQuickModal retryAfter={retryAfter} onRetry={onRetry} />));
	}

	private async handleEdit(
		payload: EditMessagePayload,
		completed: (err: RetryError | null, result?: HttpResponse<Message>, error?: unknown) => void,
	): Promise<void> {
		const {channelId, messageId, content, flags} = payload;

		const abortController = new AbortController();
		this.abortControllers.set(messageId, abortController);

		try {
			logger.debug(`Editing message ${messageId} in channel ${channelId}`);

			const body = this.buildEditRequestBody(content, flags);

			const response = await http.patch<Message>({
				url: Endpoints.CHANNEL_MESSAGE(channelId, messageId),
				body,
				signal: abortController.signal,
				rejectWithError: true,
				skipContentBlockedModal: true,
			});

			logger.debug(`Successfully edited message ${messageId} in channel ${channelId}`);
			completed(null, response);
		} catch (error) {
			const responseErr = error as ApiResponseError;
			logger.error(`Failed to edit message ${messageId} in channel ${channelId}:`, error);

			if (isRateLimitError(responseErr)) {
				this.handleEditRateLimit(responseErr, completed);
			} else {
				this.showEditErrorModal(responseErr);
				completed(null, undefined, responseErr);
			}
		} finally {
			this.abortControllers.delete(messageId);
		}
	}

	private buildEditRequestBody(content?: string, flags?: number): MessageEditRequest {
		const body: MessageEditRequest = {};

		if (content !== undefined) {
			body.content = content;
		}

		if (flags !== undefined) {
			body.flags = flags;
		}

		return body;
	}

	private handleEditRateLimit(
		error: ApiResponseError,
		completed: (err: RetryError | null, result?: HttpResponse<Message>, error?: unknown) => void,
	): void {
		const retryAfterSeconds = getApiErrorBody(error)?.retry_after ?? 0;
		const retryAfterMs = retryAfterSeconds > 0 ? retryAfterSeconds * 1000 : undefined;

		completed({retryAfter: retryAfterMs}, undefined, error);

		this.handleEditRateLimitError(retryAfterSeconds);
	}

	private showEditErrorModal(error: ApiResponseError): void {
		if (isFeatureDisabledError(error)) {
			ModalActionCreators.push(modal(() => <FeatureTemporarilyDisabledModal />));
		} else if (getApiErrorBody(error)?.code === APIErrorCodes.CONTENT_BLOCKED) {
			void import('@app/lib/ContentBlockedHandler').then((m) => m.showContentBlockedModal());
		} else {
			ModalActionCreators.push(modal(() => <MessageEditFailedModal />));
		}
	}

	private handleEditRateLimitError(retryAfter: number, onRetry?: () => void): void {
		ModalActionCreators.push(modal(() => <MessageEditTooQuickModal retryAfter={retryAfter} onRetry={onRetry} />));
	}
}

export default new MessageQueue();
